import {
  DuviSharedAdminCard as Card,
  DuviSharedAdminButton as Button,
  DuviSharedAdminInput as Input,
  DuviSharedAdminIcon as Icon,
  DuviSharedAdminBadge as Badge,
  DuviSharedAdminStatusPill as StatusPill,
  DuviSharedAdminTestIds,
} from "@duvi-agency/ui";
import {
  DuviCoreAdminTable,
  DuviCoreAdminTableSkeleton as TableSkeleton,
  DuviCoreAdminEmptyState as EmptyState,
  DuviCoreAdminMonoChip as MonoChip,
} from "../../components/duvi-core-admin-kit";
import {
  duviCoreAdminNicheStatusPill,
  duviCoreAdminNicheStatusLabel,
} from "../../lib/duvi-core-admin-mappings";
import type { NicheSummary } from "../../api/client";
import {
  DUVI_CORE_ADMIN_NICHE_ENVS,
  duviCoreAdminFormatNicheDate,
  type useDuviCoreAdminNichesList,
} from "./use-duvi-core-admin-niches-list";

type Row = NicheSummary & Record<string, unknown>;
type State = ReturnType<typeof useDuviCoreAdminNichesList>;

export function DuviCoreAdminNichesListView(state: State) {
  const {
    search,
    setSearch,
    niches,
    isLoading,
    error,
    hasNext,
    hasPrev,
    handleNext,
    handlePrev,
  } = state;

  return (
    <div
      className="flex flex-col gap-5"
      data-testid={DuviSharedAdminTestIds.nichesPage}
    >
      <Card pad>
        <div className="max-w-[380px]">
          <Input
            icon={<Icon name="search" size={16} />}
            placeholder="Search by name, short code or domain"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid={DuviSharedAdminTestIds.nicheSearch}
            aria-label="Search niches"
          />
        </div>
      </Card>

      <Card>
        {isLoading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : error ? (
          <div className="py-16 text-center text-body-sm text-error">
            Failed to load niches: {error.message}
          </div>
        ) : niches.length === 0 ? (
          <EmptyState
            icon={<Icon name="box" size={22} />}
            title={
              search.trim() ? "No niches match your search" : "No niches yet"
            }
            desc={
              search.trim()
                ? "Try a different name, short code, or domain."
                : "No provisioned niches from the Terragrunt registry yet."
            }
            testId={DuviSharedAdminTestIds.nicheEmpty}
          />
        ) : (
          <DuviCoreAdminTable<Row>
            rowKey="id"
            rowTestId={DuviSharedAdminTestIds.nicheRow}
            data={niches as Row[]}
            columns={[
              {
                key: "status",
                header: "Status",
                width: 140,
                render: (n) => (
                  <StatusPill status={duviCoreAdminNicheStatusPill(n.status)}>
                    {duviCoreAdminNicheStatusLabel(n.status)}
                  </StatusPill>
                ),
              },
              {
                key: "name",
                header: "Name",
                render: (n) => <b className="font-semibold">{n.name}</b>,
              },
              {
                key: "domain",
                header: "Domain",
                render: (n) => (
                  <span className="font-mono text-[12.5px] text-text-secondary">
                    {n.domain}
                  </span>
                ),
              },
              {
                key: "short_code",
                header: "Short code",
                width: 120,
                render: (n) => <MonoChip>{n.short_code}</MonoChip>,
              },
              {
                key: "accounts",
                header: "Accounts",
                width: 210,
                render: (n) => (
                  <span className="inline-flex gap-1.5">
                    {DUVI_CORE_ADMIN_NICHE_ENVS.map((env) => {
                      const on = Boolean(n.account_ids?.[env]);
                      return (
                        <Badge
                          key={env}
                          variant={on ? "primary" : "outline"}
                          className={on ? "" : "opacity-45"}
                        >
                          {env}
                        </Badge>
                      );
                    })}
                  </span>
                ),
              },
              {
                key: "created_at",
                header: "Created",
                width: 130,
                render: (n) => (
                  <span className="text-text-muted">
                    {duviCoreAdminFormatNicheDate(n.created_at)}
                  </span>
                ),
              },
            ]}
          />
        )}

        {!isLoading && !error && niches.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3.5 border-t border-border">
            <span
              className="text-body-sm text-text-muted"
              data-testid={DuviSharedAdminTestIds.nichePaginationInfo}
            >
              {niches.length} {niches.length === 1 ? "niche" : "niches"}
              {search.trim() ? " (filtered)" : ""}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Icon name="chevronLeft" size={15} />}
                disabled={!hasPrev}
                onClick={handlePrev}
                data-testid={DuviSharedAdminTestIds.nichePaginationPrev}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                rightIcon={<Icon name="chevronRight" size={15} />}
                disabled={!hasNext}
                onClick={handleNext}
                data-testid={DuviSharedAdminTestIds.nichePaginationNext}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
